/**
 * Curator Bridge Slice-4 decision-key lifecycle.
 *
 * The server makes a decision idempotent on `decision_key`: the same key with
 * the same intent replays the original decision instead of deciding twice, and
 * the same key with a DIFFERENT intent is refused outright. That turns key
 * management into a correctness requirement rather than a convenience:
 *
 *   - a retry of an unchanged attempt must reuse the key, or a decision that
 *     already committed (a timeout, or a 500 raised by the post-commit audit)
 *     would be applied a second time under a new key;
 *   - a changed attempt must NOT reuse the key, or the server refuses it as key
 *     reuse and the curator is blocked by their own client.
 *
 * "Unchanged" is defined here, once, as a canonical fingerprint of the complete
 * normalized intent. This module is deliberately free of React: it is a plain
 * controller so the lifecycle can be exercised directly, and so no part of it
 * can end up mutating state during a render.
 */

import type { DecisionKind, PublicationState } from './types';

export interface DecisionIntent {
  signalId: string;
  kind: DecisionKind;
  /** Already normalized: trimmed, and empty treated as absent. */
  reason: string | null;
  metadata: Record<string, unknown>;
  expectedPublicationState: PublicationState;
  /** Canonical decimal text, exactly as the server issued it. */
  expectedPublicationVersion: string;
}

/**
 * Trim, then treat empty as absent - identical to the server's normalizeReason.
 * The two must agree, because the server fingerprints the NORMALIZED reason: if
 * the client fingerprinted "ok " and the server stored "ok", adding a trailing
 * space would look like a new intent here and a replay there.
 */
export function normalizeReason(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Deterministic serialization with object keys sorted at every depth.
 *
 * `JSON.stringify` preserves insertion order, so `{a:1,b:2}` and `{b:2,a:1}` -
 * the same intent by any meaningful definition - would produce different
 * fingerprints and therefore different keys. Sorting removes that, so the
 * fingerprint depends on the metadata's content and never on how it was built.
 */
export function canonicalJson(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    // Array order IS content, so it is preserved rather than sorted.
    return `[${value.map(canonicalJson).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const body = Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
    .join(',');
  return `{${body}}`;
}

/**
 * The complete identity of one decision attempt.
 *
 * Every field the server fingerprints is included. Expected state and version
 * are part of the identity, not context around it: after a 409 the curator
 * re-reads and acts against NEWER truth, which is a different decision even
 * when the signal, kind and reason are unchanged.
 */
export function intentFingerprint(intent: DecisionIntent): string {
  return canonicalJson({
    signal_id: intent.signalId,
    kind: intent.kind,
    reason: intent.reason,
    metadata: intent.metadata,
    expected_publication_state: intent.expectedPublicationState,
    expected_publication_version: intent.expectedPublicationVersion,
  });
}

export interface DecisionAttempt {
  fingerprint: string;
  decisionKey: string;
}

/**
 * Why submission is currently refused, or null when it is open.
 *
 * The two reasons are genuinely different and must not be collapsed:
 *
 *   'conflict'      a 409. The ledger moved, so the expected state and version
 *                   in hand are stale and resending them could only reproduce
 *                   the same 409. Recovery is an authoritative re-read.
 *   'non_retryable' a refusal the server will repeat verbatim - a 400, 401, 403
 *                   or 404, or an error this client could not classify at all.
 *                   Resending the IDENTICAL request cannot succeed, and an
 *                   unknown failure may be a defect in this client rather than
 *                   anything about the request.
 *
 * A network, relay, timeout or 5xx failure sets neither: the outcome is
 * genuinely uncertain, the server is idempotent on the key, and retrying the
 * unchanged intent under the same key is the correct action.
 */
export type SubmissionBlock = 'conflict' | 'non_retryable';

/**
 * Owns exactly one key per unchanged intent.
 *
 * Not a hook and not a reducer over React state: a hook would have to be read
 * during render, and the one thing this must never do is assign a key while
 * rendering. Callers hold an instance in a ref and call it only from event
 * handlers and mutation callbacks.
 */
export interface DecisionKeyController {
  /** The key for this intent, minting one only when the intent is new. */
  keyFor(intent: DecisionIntent): string;
  /**
   * Reserves the key for a submission, or null when one must not start: a
   * request is already in flight, or a conflict is still outstanding.
   *
   * This is the only entry point a UI should use. One controller is shared by a
   * whole queue, so without a reservation two rows could submit concurrently and
   * the second would overwrite the first's attempt - leaving the in-flight
   * request's key unreachable for its own retry.
   */
  beginAttempt(intent: DecisionIntent): string | null;
  /** Releases the reservation once the request settles, however it settled. */
  settleAttempt(): void;
  /** Whether a request reserved through beginAttempt is still in flight. */
  isInFlight(): boolean;
  /**
   * Records a conflict. Submission is blocked until the block is cleared,
   * and the next attempt gets a fresh key, because the ledger has moved
   * underneath this one and any further action is a new decision.
   */
  markConflict(): void;
  /**
   * Records a refusal the server will repeat verbatim - a 400/401/403/404, or
   * an error this client could not classify.
   *
   * Blocking is scoped to THIS intent's fingerprint, not to the controller as a
   * whole. Editing the reason, switching the decision or acting on another
   * signal all produce a different fingerprint and are admitted immediately, so
   * one refused attempt never bricks the surface; only resending the identical
   * request - the one thing that provably cannot succeed - is refused.
   */
  markNonRetryable(): void;
  /** Whether a conflict is outstanding; a submit must be blocked until it clears. */
  isConflicted(): boolean;
  /** Why submission is refused, or null when it is open. */
  blockReason(): SubmissionBlock | null;
  /**
   * Clears the outstanding block, and DISCARDS the attempt that caused it.
   *
   * Discarding is the point. A caller may only clear after authoritative state
   * has been re-read, and the re-read can legitimately return the same state and
   * version as before - a transition conflict, say, rather than a version race.
   * Keeping the attempt would then let a textually identical intent reuse the
   * key the server has already spent, which it refuses as key reuse. A fresh key
   * makes the next submission the new decision it actually is.
   *
   * This is the single explicit recovery path for BOTH block reasons, so a
   * curator is never left with a surface that refuses everything.
   */
  clearConflict(): void;
  /** Forgets everything - used when the surface closes. */
  reset(): void;
  /** The current attempt, or null before the first submit. Test/diagnostic use. */
  current(): DecisionAttempt | null;
}

export function createDecisionKeyController(
  newKey: () => string,
): DecisionKeyController {
  return createIntentKeyController<DecisionIntent>(newKey, intentFingerprint);
}

/**
 * The same lifecycle as DecisionKeyController, for any intent a caller can
 * fingerprint. Every member means exactly what it means there; only the intent
 * type and the definition of "unchanged" are supplied by the caller.
 */
export interface IntentKeyController<T> {
  keyFor(intent: T): string;
  beginAttempt(intent: T): string | null;
  settleAttempt(): void;
  isInFlight(): boolean;
  markConflict(): void;
  markNonRetryable(): void;
  isConflicted(): boolean;
  blockReason(): SubmissionBlock | null;
  clearConflict(): void;
  reset(): void;
  current(): DecisionAttempt | null;
}

/**
 * The one controller implementation. createDecisionKeyController delegates here
 * with the publication-decision fingerprint, so a second intent type - a
 * practice assignment, say - reuses this lifecycle rather than restating it.
 *
 * `fingerprintOf` must be a complete, canonical identity of the intent: any
 * field the server fingerprints and this omits would let a changed intent reuse
 * a spent key.
 */
export function createIntentKeyController<T>(
  newKey: () => string,
  fingerprintOf: (intent: T) => string,
): IntentKeyController<T> {
  let attempt: DecisionAttempt | null = null;
  let block: SubmissionBlock | null = null;
  let inFlight = false;
  // The fingerprint a non-retryable refusal applies to. Scoping the refusal to
  // one intent is what keeps the surface usable: a different intent is a
  // different request, and the server has said nothing about it.
  let refusedFingerprint: string | null = null;

  const keyFor = (intent: T): string => {
    const fingerprint = fingerprintOf(intent);
    // Reuse only when the intent is byte-identical AND nothing is blocking.
    // Either condition failing makes this a new decision.
    if (attempt !== null && attempt.fingerprint === fingerprint && block === null) {
      return attempt.decisionKey;
    }
    attempt = { fingerprint, decisionKey: newKey() };
    block = null;
    return attempt.decisionKey;
  };

  return {
    keyFor,
    beginAttempt(intent: T): string | null {
      if (inFlight) return null;
      // A conflict blocks the whole surface: the expected state and version in
      // hand came from a read the ledger has since moved past, so nothing built
      // on them may be sent until an authoritative re-read replaces them.
      if (block === 'conflict') return null;
      // A non-retryable refusal blocks only the request it was about. Resending
      // the IDENTICAL intent would collect the identical answer, so it is
      // refused here; a genuinely different intent is something the server has
      // said nothing about, and refusing it too would brick the surface over
      // one bad request.
      if (block === 'non_retryable') {
        if (
          refusedFingerprint !== null &&
          fingerprintOf(intent) === refusedFingerprint
        ) {
          return null;
        }
        block = null;
        refusedFingerprint = null;
      }
      const key = keyFor(intent);
      inFlight = true;
      return key;
    },
    settleAttempt(): void {
      inFlight = false;
    },
    isInFlight(): boolean {
      return inFlight;
    },
    markConflict(): void {
      block = 'conflict';
    },
    markNonRetryable(): void {
      block = 'non_retryable';
      refusedFingerprint = attempt?.fingerprint ?? null;
    },
    isConflicted(): boolean {
      return block === 'conflict';
    },
    blockReason(): SubmissionBlock | null {
      return block;
    },
    clearConflict(): void {
      block = null;
      attempt = null;
      refusedFingerprint = null;
    },
    reset(): void {
      attempt = null;
      block = null;
      inFlight = false;
      refusedFingerprint = null;
    },
    current(): DecisionAttempt | null {
      return attempt;
    },
  };
}

/**
 * The failure policy, applied to the controller.
 *
 * Extracted so the hook and the tests exercise the SAME decision rather than two
 * descriptions of it: a test that re-implemented this mapping would prove only
 * that the test agrees with itself.
 *
 * Order matters. A 409 is also non-retryable, but it is a conflict first: its
 * recovery is an authoritative re-read, and its next attempt must be freshly
 * keyed against newer truth.
 */
export function applyFailureToController(
  controller: DecisionKeyController,
  failure: { isConflict: boolean; retryable: boolean },
): SubmissionBlock | null {
  if (failure.isConflict) {
    controller.markConflict();
    return 'conflict';
  }
  if (!failure.retryable) {
    controller.markNonRetryable();
    return 'non_retryable';
  }
  // Uncertain outcome: the same key may be sent again, so nothing is blocked.
  return null;
}

/**
 * The browser's UUID generator, isolated so a test can substitute a counter and
 * assert key identity across attempts rather than merely asserting "some uuid".
 */
export function browserDecisionKey(): string {
  return crypto.randomUUID();
}
